// ** React imports
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// ** Design System imports
import { SkeletonLoading } from '@saleshandy/design-system';
import { Placement } from '../../../design-system/components/overlay';
import Select from '../../../design-system/components/select';

// ** Custom Imports
import { DialerCall } from '../../../svg/dialer-call';
import CountryCodePhoneInputPicker from '../../country-code-phone-input-picker/country-code-phone-input-picker';
import type { CallViaOption } from '../types';
import { getDialerPhoneNumberListRequest } from '../../../../components/dialer/extra-actions';
import { setIsDialerLocalPresenceEnabled } from '../../../../components/home/home-slice';
import { RootState } from '../../../../store/root-reducer';
import { AlertCircleIcon } from './pre-call-test/microphone-access-warning-modal';
import { accessibleOnClick } from '../../../utils';
import { setShowMicrophoneAccessWarningModal } from '../../../../components/dialer/dialer-slice';

// ** Constants
const KEYPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', ''];

// ** Types
interface DialerKeypadProps {
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  selectedCountryISOCode: string;
  selectedCountryCode: string; // Dialing code e.g. +1, +91
  onCountryChange: (countryCode: string) => void;
  callVia: CallViaOption[];
  selectedCallVia: CallViaOption;
  onCallViaChange: (callVia: CallViaOption) => void;
  onCallClick: () => void;
  onNumberClick: (number: string) => void;
  isCallViaLoading?: boolean;
  pipContainer?: HTMLElement | null;
}

/**
 * @description Dialer Keypad Component which contains the keypad, call via dropdown, and call button
 * @param props - DialerKeypadProps
 * @returns React.ReactNode
 */
const DialerKeypad: React.FC<DialerKeypadProps> = ({
  phoneNumber,
  onPhoneNumberChange,
  selectedCountryISOCode = 'US',
  selectedCountryCode = '+1',
  onCountryChange,
  callVia,
  selectedCallVia,
  onCallViaChange,
  onCallClick,
  onNumberClick,
  isCallViaLoading = false,
  pipContainer = null,
}) => {
  const DEBOUNCE_MS = 500;
  const dispatch = useDispatch();
  // Prepare call via options for Select
  const callViaOptions = useMemo(
    () =>
      callVia?.map((via) => ({
        key: via?.id,
        value: via?.value,
        label: via?.label,
        isPrimary: via?.isPrimary,
        data: via,
      })),
    [callVia],
  );
  const callViaOptionsHasData = callViaOptions?.length === 0;

  const adminSettings = useSelector(
    (state: RootState) => state?.adminSettings?.adminSettings,
  );
  const isDemoAccount = useSelector(
    (state: RootState) => state?.home?.isDemoAccount,
  );
  const isDialerLocalPresenceEnabledSettingChanged = adminSettings?.find(
    (item) => item?.code === 'dialer-local-presence-enabled',
  )?.value;

  const [micPermissionEnabled, setMicPermissionEnabled] = useState(true);

  // ** Handlers **
  const handleCountryChange = useCallback(
    (countryCode: string) => {
      onCountryChange(countryCode);
    },
    [onCountryChange],
  );

  const handleCallActionClick = useCallback(() => {
    if (selectedCallVia) {
      onCallClick();
    }
  }, [selectedCallVia, onCallClick]);

  const handleFixItClick = useCallback(() => {
    dispatch(setShowMicrophoneAccessWarningModal(true));
  }, []);

  // Debounced API call when phoneNumber length > 5 (on digit add/remove)
  useEffect(() => {
    if (phoneNumber?.length <= 5) return;

    const timeoutId = setTimeout(() => {
      dispatch(
        getDialerPhoneNumberListRequest({
          prospectPhoneNumber: `${selectedCountryCode}${phoneNumber}`,
        }),
      );
    }, DEBOUNCE_MS);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [selectedCountryCode, phoneNumber, dispatch]);

  useEffect(() => {
    dispatch(
      setIsDialerLocalPresenceEnabled(
        isDialerLocalPresenceEnabledSettingChanged !== '0',
      ),
    );
  }, [isDialerLocalPresenceEnabledSettingChanged, dispatch]);

  const checkMicPermission = useCallback(async () => {
    // Function to only if microphone permission is granted or denied
    try {
      await navigator?.mediaDevices?.getUserMedia({
        audio: true,
      });
      setMicPermissionEnabled(true);
    } catch {
      setMicPermissionEnabled(false);
    }
  }, []);

  useEffect(() => {
    checkMicPermission();
  }, []);

  return (
    <div className="dialer-keypad">
      {/* Warning banner for no microphone access */}
      {!micPermissionEnabled && (
        <div className="dialer-keypad-warning-banner">
          <div className="text-container">
            <AlertCircleIcon width={16} height={16} />
            <p>Cannot access microphone</p>
          </div>
          <button
            className="fix-it-btn"
            type="button"
            {...accessibleOnClick(handleFixItClick)}
          >
            Fix it
          </button>
        </div>
      )}
      {/* Phone Number Input - Integrated Design */}
      <div className="dialer-phone-input-container">
        <CountryCodePhoneInputPicker
          phoneNumber={phoneNumber}
          onPhoneNumberChange={onPhoneNumberChange}
          defaultCountryISOCode={selectedCountryISOCode || 'US'}
          onCountryChange={(country) => {
            handleCountryChange(country?.iso?.toUpperCase() || 'US');
          }}
        />
      </div>
      <div className="dialer-keypad-grid-container">
        {/* Dial Pad */}
        <div className="dialer-keypad-grid">
          {KEYPAD_KEYS.map((num) => (
            <button
              key={num}
              type="button"
              className={`dialer-keypad-btn ${num === '' ? 'no-btn' : ''}`}
              onClick={() => (num ? onNumberClick(num) : {})}
            >
              <span className="dialer-keypad-btn-number">{num}</span>
            </button>
          ))}
        </div>

        {/* Call Via Dropdown */}
        {micPermissionEnabled && (
          <div className="dialer-call-via-container">
            <p>Call Via</p>
            <div className="dialer-call-via-select-container">
              {isCallViaLoading ? (
                <div className="dialer-call-via-skeleton">
                  <SkeletonLoading width={220} height={30} />
                </div>
              ) : (
                <Select
                  className={`dialer-call-via-select ${
                    callViaOptionsHasData
                      ? 'dialer-call-via-select-disabled'
                      : ''
                  }`}
                  options={callViaOptions}
                  selectedOptionKey={selectedCallVia?.id}
                  optionRenderer={(option) => <span>{option?.label}</span>}
                  selectedOptionRenderer={([option]) => (
                    <span>{option?.label}</span>
                  )}
                  onChange={([option]) => {
                    if (option?.data) {
                      onCallViaChange(option?.data);
                    }
                  }}
                  placement={Placement.Top}
                  showSearch
                  searchPlaceholder="Search"
                  filterOption={(value, option) =>
                    option?.label?.toLowerCase()?.includes(value?.toLowerCase())
                  }
                  container={pipContainer ?? undefined}
                />
              )}
            </div>
          </div>
        )}

        {/* Call Button */}
        {isDemoAccount ? (
          <div className="dialer-call-btn-tooltip-wrapper">
            <button
              type="button"
              className="dialer-call-btn"
              {...accessibleOnClick(handleCallActionClick)}
              disabled
            >
              <DialerCall width={16} height={16} />
            </button>
            <span className="dialer-call-btn-tooltip">
              Calling is disabled in demo account
            </span>
          </div>
        ) : (
          <button
            type="button"
            className="dialer-call-btn"
            {...accessibleOnClick(handleCallActionClick)}
            disabled={
              !micPermissionEnabled ||
              callViaOptionsHasData ||
              isCallViaLoading ||
              phoneNumber?.length > 15 ||
              phoneNumber?.length < 5 ||
              !/\d/?.test(phoneNumber ?? '')
            }
          >
            <DialerCall width={16} height={16} />
          </button>
        )}
      </div>
    </div>
  );
};
export default memo(DialerKeypad);
